import React, { useContext, useEffect, useRef, useState } from 'react';
import { Button, ConfigProvider, message, Modal } from 'antd';
import { css } from '@emotion/css';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';

import API from 'src/apis';
import { getFeatureByPlan } from 'src/helpers/rest';
import { BASE, GOOGLE_FORMS, SUBSCRIPTION_CONST } from 'src/common/constants';

import styles from './index.module.scss';

interface SubscriptionCardProps {
    subscription: any;
    startTimer: Function;
    isPollingActive: boolean;
}

const SubscriptionCard: React.FunctionComponent<SubscriptionCardProps> = (props) => {

    const { subscription, startTimer, isPollingActive } = props;
    const { trp_type, trp_amount, trp_period, trp_actual_amount, subscribed_plan, trp_id, subscribed_pro = false } = subscription;
    const { BASIC, PREMIUM, ENTERPRISE, YEARLY } = SUBSCRIPTION_CONST;

    const { getPrefixCls } = useContext(ConfigProvider.ConfigContext);
    const rootPrefixCls = getPrefixCls();
    const linearGradientButton = css`
        &.${rootPrefixCls}-btn-primary:not([disabled]):not(.${rootPrefixCls}-btn-dangerous) {
            border-width: 0;
            border-radius: 30px;
            height: 60px;
            font-size: 22px;
            font-weight: 600;
            color: #8800ff;
            margin-bottom: 10px;

            > span {
                position: relative;
            }

            &::before {
                content: '';
                background: linear-gradient(45deg, #ffffff, #deccff 80%);
                position: absolute;
                inset: 0;
                opacity: 1;
                transition: all 0.3s;
                border-radius: inherit;
            }

            &:hover::before {
                opacity: 0;
            }
        }
    `;

    const featureList = getFeatureByPlan(trp_type);
    const modalTextRef = useRef<HTMLDivElement | null>(null);

    const [loading, setLoading] = useState<boolean>(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [subscriptionUrl, setSubscriptionUrl] = useState('');
    const [iframeHeight, setIframeHeight] = useState<string>('100vh');

    const onPressCreateSubscription = async () => {
        try {
            if (subscribed_plan || trp_type === BASIC || loading || !trp_id || isPollingActive || subscribed_pro) return;
            if (trp_type === ENTERPRISE) {
                window.open(GOOGLE_FORMS.CONTACT_US, '_blank');
                return;
            }
            const userDetails = JSON.parse(localStorage.getItem('userDetails') as string);
            if (!userDetails) {
                message.error({ content: 'User not found! Please try again after refreshing the page.' });
                return;
            }
            setLoading(true);

            const url = new URL(BASE.API_HOST);
            url.searchParams.append('plan_id', trp_id);
            url.searchParams.append('email', userDetails.email);

            const subscriptionData = await API.CreateSubscription(url.search);
            if (subscriptionData.subscription_url) {
                setSubscriptionUrl(subscriptionData.subscription_url);
                setIsModalVisible(true);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!modalTextRef.current || !modalTextRef.current.offsetHeight) return;

        const modalTextHeight = modalTextRef.current.offsetHeight;
        const calculatedIframeHeight = `calc(100vh - ${modalTextHeight + 100}px)`;
        setIframeHeight(calculatedIframeHeight);
    }, [isModalVisible, modalTextRef.current?.offsetHeight]);

    const handleCancel = () => {
        startTimer();
        setIsModalVisible(false);
    };

    return (
        <>
            <div className={styles.box_wrapper}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <div className={styles.plan_name} style={trp_type === BASIC ? { fontWeight: '600', lineHeight: '100px', fontSize: '48px' } : {}}>{trp_type}</div>
                    {subscribed_plan
                        && <div className={styles.current_plan} style={trp_type === BASIC ? { marginBottom: '72px' } : {}}>Subscribed</div>}
                    {!!(trp_type === PREMIUM && trp_period === YEARLY && !subscribed_plan)
                        && <div className={styles.best_deal}>#bestdeal</div>}
                </div>
                {/* {!!(trp_actual_amount && trp_type === PREMIUM) &&
                <div>
                    {trp_type === PREMIUM && <span className={styles.plan_actual_price}>&#8377; </span>}
                    <span className={styles.plan_actual_price}>{trp_actual_amount}</span>
                    <span className={styles.plan_discount_price}> ( &#8377;{trp_amount / 12}/month )</span>
                </div>
            } */}
                {(trp_amount && trp_type != BASIC)
                    ? <div style={{ width: '300px' }}>
                        {trp_type === PREMIUM && <span className={styles.plan_price} style={trp_actual_amount ? { lineHeight: '50px', fontSize: '48px' } : {}}>&#8377; </span>}
                        {/* <span className={styles.plan_price} style={trp_actual_amount ? { lineHeight: '50px', fontSize: '48px' } : {}}>{trp_period === YEARLY ? trp_amount / 12 : trp_amount}</span> */}
                        <span className={styles.plan_price}>{trp_period === YEARLY ? trp_amount : trp_amount}</span>
                        {trp_type != BASIC && <span className={styles.plan_period}>{
                            trp_period === YEARLY
                                ? <>
                                    <span style={{ fontSize: "12px" }}>/year</span>
                                    <span> (excluding 18% GST)</span>
                                </>
                                : <>
                                <span style={{ fontSize: "12px" }}>/month</span>
                                <span> (excluding 18% GST)</span>
                            </>
                        }
                        </span>}
                    </div>
                    : <>
                        {trp_type != BASIC
                            ? <span className={styles.plan_contact}>Contact Sales</span>
                            : <></>
                        }
                    </>
                }

                <span className={styles.plan_desc}>
                    {trp_type === BASIC
                        ? 'Free credits to revolutionize your research.'
                        : 'Get unlimited credits to revolutionize your research.'}
                </span>

                <ul className={styles.features}>
                    {featureList.map((feature, index) => (
                        <li style={{ display: 'flex', justifyContent: 'space-between' }} key={index} className={feature.available ? styles.available : styles.not_available}>
                            {feature.text} {feature.available ? <CheckOutlined /> : <CloseOutlined />}
                        </li>
                    ))}
                </ul>

                <ConfigProvider
                    button={{ className: linearGradientButton }}
                >
                    <Button
                        type='primary'
                        size='large'
                        style={{
                            background: 'linear-gradient(45deg, #deccff, #ffffff 80%)',
                            borderRadius: '30px',
                            height: '60px',
                            fontSize: subscribed_pro ? '18px' : '22px',
                            fontWeight: (subscribed_plan || trp_type === BASIC || isPollingActive || subscribed_pro) ? 500 : 600,
                            color: (subscribed_plan || trp_type === BASIC || isPollingActive || subscribed_pro) ? 'gray' : '#8800ff',
                            marginBottom: '10px',
                            cursor: (subscribed_plan || trp_type === BASIC || isPollingActive || subscribed_pro) ? 'no-drop' : 'pointer',
                            padding: 'auto 10px'
                        }}
                        onClick={onPressCreateSubscription}
                        loading={loading}
                    >
                        {subscribed_plan
                            ? 'Your Current Plan'
                            : <>
                                {trp_type === BASIC ? 'Start for free' : trp_type === ENTERPRISE ? 'Contact Us' : subscribed_pro ? 'Yearly Subscription Activated' : 'Get Unlimited'}
                            </>
                        }
                    </Button>
                </ConfigProvider>
            </div>

            {isModalVisible &&
                <Modal
                    visible={isModalVisible}
                    footer={null}
                    centered
                    width='100vw'
                    style={{ top: 0 }}
                    closable={false}
                >
                    <div style={{ padding: '10px 0px' }} ref={modalTextRef}>
                        <div style={{ backgroundColor: 'rgb(255, 237, 215)', border: '1px solid #C67D00FC', fontSize: '16px', textAlign: 'center', padding: '10px 20px', color: '#454551' }}>
                            Please do not close or refresh this window while your payment is being processed. Once your payment is successful, you may close this window. We will process your payment and update you with the status shortly.
                        </div>
                    </div>
                    <iframe
                        src={subscriptionUrl}
                        style={{ width: '100%', height: iframeHeight, border: 'none' }}
                        loading='eager'
                    />
                    <Button
                        style={{ position: 'absolute', top: 5, right: 5, background: 'transparent', color: 'black', border: 'none', borderRadius: '50%', width: '30px', height: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1001 }}
                        onClick={handleCancel}
                    >
                        X
                    </Button>
                </Modal>}
        </>
    );
}

export default SubscriptionCard;